(ns common.schema.entities.workspace.kyc.templates.capman.ubo
  (:require [common.schema.entities.workspace.kyc.kyc-options :as kyc-options]
            [common.schema.entities.workspace.kyc.questions.questions-registry :as questions-registry]
            [common.schema.entities.workspace.kyc.templates.capman.pep :as pep]
            [common.schema.entities.workspace.kyc.templates.templates :as templates]))

(def ^:private ubo-categories-options
  [{:key :ceo
    :ord 1.0
    :translations {:en "CEO"
                   :fi "Toimitusjohtaja"}}
   {:key :chair-of-the-board
    :ord 2.0
    :translations {:en "Chair of the board"
                   :fi "Hallituksen puheenjohtaja"}}
   {:key :board-member-and-shares-over-25
    :ord 3.0
    :translations {:en "Board member and more than 25% share ownership"
                   :fi "Hallituksen jäsen ja yli 25% omistusoikeus"}}
   {:key :board-member-and-voting-rights-over-25
    :ord 4.0
    :translations {:en "Board member and more than 25% voting rights"
                   :fi "Hallituksen jäsen ja yli 25% äänivalta"}}
   {:key :shares-over-25
    :ord 5.0
    :translations {:en "Over 25% share ownership"
                   :fi "Yli 25% omistusoikeus"}}
   {:key :voting-rights-over-25
    :ord 6.0
    :translations {:en "Over 25% voting rights"
                   :fi "Yli 25% äänivalta"}}
   {:key :right-to-dismiss-board
    :ord 7.0
    :translations {:en "Right to nominate or dismiss the majority of the board members"
                   :fi "Oikeus nimittää tai erottaa enemmistö hallituksen jäsenistä"}}
   {:key :vice-chair-of-the-board
    :ord 8.0
    :translations {:en "Vice chair of the board"
                   :fi "Hallituksen varapuheenjohtaja"}}
   {:key :board-member
    :ord 9.0
    :translations {:en "Board member"
                   :fi "Hallituksen jäsen"}}
   ;; Deprecated in DEV-8071 — replaced by the nine specific options above.
   ;; Kept so existing answers still render. `:deprecated?` makes the dropdown
   ;; render it disabled when it is the current value (and hide it otherwise),
   ;; drops it on answers-copy at process creation, and treats it as missing in
   ;; validation — forcing the responder to re-categorize.
   {:key :mob-ceo-or-similar
    :ord 99.0
    :deprecated? true
    :translations {:en "Board member, CEO or equivalent position"
                   :fi "Hallituksen jäsen, toimitusjohtaja tai vastaava asema"}}])

(def ^:private ownership-percent-categories
  #{:shares-over-25 :board-member-and-shares-over-25})

(def ^:private voting-rights-percent-categories
  #{:voting-rights-over-25 :board-member-and-voting-rights-over-25})

(def ^:private address-categories
  #{:shares-over-25
    :voting-rights-over-25
    :right-to-dismiss-board
    :board-member-and-shares-over-25
    :board-member-and-voting-rights-over-25})

(def ^:private identification-categories
  "Categories where the UBO is only a board member with no shareholding role
   (vice chair, plain board member) do not require identification."
  #{:ceo
    :chair-of-the-board
    :shares-over-25
    :voting-rights-over-25
    :right-to-dismiss-board
    :board-member-and-shares-over-25
    :board-member-and-voting-rights-over-25
    ;; Legacy answers continue to show identification
    :mob-ceo-or-similar})

(defn address-shown? [answers]
  (contains? address-categories (:kyc-ubo/primary-ubo-category answers)))

(defn identification-needed?
  [answers _]
  (->> answers
       :kyc-ubo/primary-ubo-category
       (contains? identification-categories)))

(defn- not-draft? [kyc-process]
  (not= (:kyc-process/status kyc-process) :kyc-process.status/draft))

(def schema
  {:kyc-template/template-name
   "UBO"
   :kyc-template/kyc-form-preface
   {:translations {:en "Ultimate beneficial owners (UBO)"
                   :fi "Yhteisön tosiasialliset edunsaajat"}}

   :kyc-ubo.group/ubo-position
   {:ord               1.0
    :translations      {:en "Reason for UBO"
                        :fi "Peruste"}
    :conditional-block (fn [_answers kyc-process]
                         (not (templates/natural-person-templates-keys (:kyc-process/template kyc-process))))
    :questions         (questions-registry/resolve-questions
                        [[:kyc-ubo/primary-ubo-category
                          {:options ubo-categories-options
                           :translations {:en "This person is considered a UBO due to"
                                          :fi "Henkilöä pidetään tosiasiallisena edunsaajana seuraavalla perusteella"}}]
                         [:kyc-ubo/ubo-category-ownership
                          {:conditional-field (fn [answers]
                                                (contains? ownership-percent-categories
                                                           (:kyc-ubo/primary-ubo-category answers)))}]
                         [:kyc-ubo/ubo-category-voting-rights
                          {:conditional-field (fn [answers]
                                                (contains? voting-rights-percent-categories
                                                           (:kyc-ubo/primary-ubo-category answers)))}]
                         [:kyc-ubo/ubo-category-position
                          ;; Only shown for the legacy :mob-ceo-or-similar value so
                          ;; existing answers still surface the previously entered
                          ;; position description. New selections use the specific
                          ;; CEO / Chair / Vice chair / Board member categories.
                          {:mandatory-field? (fn [answers kyc-process]
                                               (and (= :mob-ceo-or-similar
                                                       (:kyc-ubo/primary-ubo-category answers))
                                                    (not-draft? kyc-process)))}]])}

   :kyc-ubo.group/basic-information
   {:ord          2.0
    :translations {:en "Basic information"
                   :fi "Perustiedot"}
    :questions    (questions-registry/resolve-questions
                   [[:kyc-ubo/last-name]
                    [:kyc-ubo/first-name]
                    [:kyc-ubo/middle-names]
                    [:kyc-ubo/dob
                     {:mandatory-field? (fn [_answers kyc-process]
                                          (not= (:kyc-process/status kyc-process)
                                                :kyc-process.status/draft))}]
                    [:kyc-ubo/pob]
                    [:kyc-ubo/cob]
                    [:kyc-ubo/address
                     {:conditional-field address-shown?
                      :mandatory-field?  (fn [answers kyc-process]
                                           (and (address-shown? answers)
                                                (not-draft? kyc-process)))}]
                    [:kyc-ubo/zip-code
                     {:conditional-field address-shown?
                      :mandatory-field?  (fn [answers kyc-process]
                                           (and (address-shown? answers)
                                                (not-draft? kyc-process)))}]
                    [:kyc-ubo/city
                     {:conditional-field address-shown?
                      :mandatory-field?  (fn [answers kyc-process]
                                           (and (address-shown? answers)
                                                (not-draft? kyc-process)))}]
                    [:kyc-ubo/country
                     {:conditional-field address-shown?
                      :mandatory-field?  (fn [answers kyc-process]
                                           (and (address-shown? answers)
                                                (not-draft? kyc-process)))
                      :translations      {:en "Country of residence"
                                          :fi "Asuinmaa"}}]
                    [:kyc-ubo/nationalities]])}

   :kyc-ubo.group/tax-residencies
   {:ord          3.0
    :translations {:en "Tax Residency"
                   :fi "Verovelvollisuus"}
    :info         {:en ""
                   :fi "Lisää myös valtiot, joihin henkilö maksaa tulo- tai kiinteistöveroa"}
    :questions    (questions-registry/resolve-questions
                   [[:kyc-ubo/tax-countries]
                    [:kyc-ubo/usa-tax-residence]
                    [:kyc-ubo/non-usa-confirmation]
                    [:kyc-ubo/usa-confirmation]])}

   :kyc-ubo.group/pep
   {:ord          4.0
    :translations {:en "Politically exposed person"
                   :fi "Poliittinen vaikutusvalta"}
    :info         (->> pep/schema
                       :kyc-pep.group/basic-information
                       :info)
    :questions    (questions-registry/resolve-questions
                   [[:kyc-ubo/pep]
                    [:kyc-ubo.pep/country {:conditional-field (fn [answers]
                                                                (= :yes (:kyc-ubo/pep answers)))}]
                    [:kyc-ubo.pep/political-function {:conditional-field (fn [answers]
                                                                           (= :yes (:kyc-ubo/pep answers)))}]
                    [:kyc-ubo.pep/relationship-to-ubo {:conditional-field (fn [answers]
                                                                            (= :yes (:kyc-ubo/pep answers)))}]
                    [:kyc-ubo.pep/name {:conditional-field (fn [answers]
                                                             (and (= :yes (:kyc-ubo/pep answers))
                                                                  (some #{(:kyc-ubo.pep/relationship-to-ubo answers)}
                                                                        [:family-member :other])))}]
                    [:kyc-ubo.pep/connection-to-investor {:conditional-field (fn [answers kyc-process]
                                                                               (and (not (templates/natural-person-templates-keys (:kyc-process/template kyc-process)))
                                                                                    (= :yes (:kyc-ubo/pep answers))
                                                                                    (some #{(:kyc-ubo.pep/relationship-to-ubo answers)}
                                                                                          [:family-member :other])))}]])}
   :kyc-ubo.group/identification
   {:ord          5.0
    :translations {:en "Identification"
                   :fi "Tunnistetiedot"}
    :info         {:en ""
                   :fi ""}
    :conditional-block identification-needed?
    :questions
    (questions-registry/resolve-questions
     (let [old-id-filled-fn (fn [answers _]
                              (-> answers
                                  :kyc-ubo/identification-attachment))]
       (concat
        [[:kyc-ubo/type-of-identification-attachment
          {:options kyc-options/capman-identification-options
           :mandatory-field? (fn [answers kyc-process]
                               (and (identification-needed? answers kyc-process)
                                    (not-draft? kyc-process)))}]
         [:kyc-ubo/identification-expires
          {:mandatory-field? (fn [answers kyc-process]
                               (and (identification-needed? answers kyc-process)
                                    (not-draft? kyc-process)))}]

;; removed in DEV-7881
         [:kyc-ubo/identification-attachment
          {:skip-previous-answers-copy? true
           :conditional-field           old-id-filled-fn}]]

        (let [condition-fn (fn [answers kyc-process]
                             (and (not (old-id-filled-fn answers kyc-process))
                                  (identification-needed? answers kyc-process)
                                  (-> answers
                                      :kyc-ubo/type-of-identification-attachment
                                      (= :identification.type/passport))))]
          [[:kyc-ubo/passport-photo-page-file
            {:conditional-field condition-fn
             :mandatory-field?  (fn [answers kyc-process]
                                  (and (condition-fn answers kyc-process)
                                       (not-draft? kyc-process)))
             :translations      {:en "Copy of a certified passport (photo page)"
                                 :fi "Oikeaksi todistettu passikopio (kuvasivu)"}}]])

        (let [condition-fn (fn [answers kyc-process]
                             (and (not (old-id-filled-fn answers kyc-process))
                                  (-> answers
                                      :kyc-ubo/type-of-identification-attachment
                                      (= :identification.type/id-card))))
              base-conf {:conditional-field condition-fn
                         :mandatory-field?  (fn [answers kyc-process]
                                              (and (condition-fn answers kyc-process)
                                                   (not-draft? kyc-process)))}]
          [[:kyc-ubo/national-id-card-front-side-file
            (assoc base-conf :translations {:en "Copy of a certified national ID card (front side)"
                                            :fi "Oikeaksi todistettu henkilökortin kopio (etuosa)"})]
           [:kyc-ubo/national-id-card-back-side-file
            (assoc base-conf :translations {:en "Copy of a certified national ID card (back side)"
                                            :fi "Oikeaksi todistettu henkilökortin kopio (takaosa)"})]]))))}})
